import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import './pdfViewer.scss';

// Worker matched to the installed pdfjs-dist version, served from CDN so we
// don't have to wire the .mjs worker asset through CRA's bundler. unpkg serves
// the exact published version straight from npm, so it always matches whatever
// pdfjs-dist react-pdf pulled in. (Only the public PDF.js worker script comes
// from the CDN — never the document itself.)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Everything the viewer needs to render one PDF. */
export interface PdfSource {
  /** Short-lived inline URL for the file. */
  url: string;
  /** Set for password-protected PDFs so the reader opens without prompting. */
  password?: string;
}

interface Props {
  /** Shown in the title bar — the document or report name. */
  title: string;
  /** Identity of what's on screen; changing it re-runs `load`. */
  sourceKey: string | number;
  /** Fetches the file's short-lived inline URL (and password, if protected). */
  load: () => Promise<PdfSource>;
  /** What the user can do when the file has gone missing from storage —
   *  e.g. "Regenerate the report to create a new PDF." */
  missingHint?: string;
  onClose: () => void;
}

// pdf.js reports its failures as typed exceptions; each one has a different
// answer for the user, so say which happened rather than "couldn't be
// displayed" for all of them.
const describeLoadError = (err: unknown): { message: string; missing: boolean } => {
  const e = err as { name?: string; message?: string } | undefined;
  const name = e?.name ?? '';
  const message = e?.message ?? '';
  if (name === 'MissingPDFException' || /\b404\b/.test(message)) {
    return { message: 'This file is no longer in storage.', missing: true };
  }
  if (name === 'PasswordException') {
    return { message: "This PDF is password-protected and the stored password didn't open it.", missing: false };
  }
  if (name === 'InvalidPDFException') {
    return { message: "This file isn't a readable PDF.", missing: false };
  }
  return { message: "This file couldn't be displayed.", missing: false };
};

// View-only PDF viewer, shared by Documents and Reports. Renders pages to
// canvas only (text/annotation layers disabled — that also means no
// selectable/copyable text), no download/print affordance, right-click
// suppressed. The file is fetched over a short-lived presigned URL just to
// render; see the documents feature plan §2 for the honest scope of this
// (deterrence, not a hard guarantee).
const PdfViewer: React.FC<Props> = ({ title, sourceKey, load, missingHint, onClose }) => {
  const [source, setSource] = useState<PdfSource | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Set when the file itself is gone, so the caller's hint only shows for the
  // failure it actually answers.
  const [isMissing, setIsMissing] = useState(false);

  // Held in a ref so an inline `load` closure at the call site doesn't refetch
  // on every parent render — `sourceKey` is the one thing that means "new file".
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setSource(null);
    setNumPages(0);
    setError(null);
    setIsMissing(false);
    loadRef.current()
      .then((s) => { if (!cancelled) setSource(s); })
      .catch(() => { if (!cancelled) setError("This file couldn't be opened."); });
    return () => { cancelled = true; };
  }, [sourceKey]);

  // Escape closes, as with any modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // react-pdf reloads the document whenever `file`/`options` change identity,
  // so both must be stable for a given source.
  const file = useMemo(() => (source ? source.url : null), [source]);
  const options = useMemo(
    () => (source?.password ? { password: source.password } : undefined),
    [source]
  );

  const pageWidth = Math.min(860, (typeof window !== 'undefined' ? window.innerWidth : 900) - 140);

  // Rendered into <body> rather than where it's mounted. Every caller sits
  // inside something that establishes a stacking context — the press bay is
  // `position: relative; z-index: 20`, the menu rail 60 — and a `position:
  // fixed` child cannot escape one. The backdrop's own z-index is then only
  // ever relative to that context, so the viewer was being capped below the
  // app header's 1001 and sliding under the top banner. No number would have
  // fixed it; leaving the tree does.
  return createPortal(
    <div className="pdfv-backdrop" onClick={onClose}>
      <div
        className="pdfv"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="pdfv__head">
          <div className="pdfv__heading">
            <h3 className="pdfv__title" title={title}>{title}</h3>
            {numPages > 0 && (
              <span className="pdfv__pages">{numPages} {numPages === 1 ? 'page' : 'pages'}</span>
            )}
          </div>
          <button type="button" className="pdfv__close" onClick={onClose} title="Close" aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="pdfv__stage">
          {error ? (
            <div className="pdfv__status">
              <span>{error}</span>
              {isMissing && missingHint && <span className="pdfv__status-hint">{missingHint}</span>}
            </div>
          ) : file ? (
            <Document
              file={file}
              options={options}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={(err) => {
                const { message, missing } = describeLoadError(err);
                setIsMissing(missing);
                setError(message);
              }}
              loading={<div className="pdfv__status"><div className="pdfv__spinner" />Loading…</div>}
              error={<div className="pdfv__status">This file couldn't be displayed.</div>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i} className="pdfv__page">
                  <Page
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              ))}
            </Document>
          ) : (
            <div className="pdfv__status"><div className="pdfv__spinner" />Loading…</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PdfViewer;
